/**
 * Decide whether a simulation's ignitions were laid out on a regular grid,
 * and if so at what spacing, so the Risk From layer (and its story-map and
 * inspection mirrors) can draw them as contiguous cells rather than points.
 *
 * The previous detector sorted raw latitudes and judged the gaps between
 * neighbours. Ensemble runs replicate every ignition once per member, so two
 * point ignitions × M members produced one non-zero gap per axis — trivially
 * "consistent" — and the pair was drawn as two giant cells spanning the
 * distance between them (fisci-front-end #283). This version works on
 * distinct locations and additionally checks that those locations actually
 * sit on the lattice the candidate spacing implies.
 */

export interface GridCandidate {
  /** Raster step from the results, used when no grid is detected. */
  latStep?: number | null;
  lngStep?: number | null;
  ignitions: Array<{ lat: number | null | undefined; lng: number | null | undefined }>;
}

export interface GridDetection {
  latStep: number;
  lngStep: number;
  isGridded: boolean;
}

/** Fewest distinct locations that can be called a grid (2 × 2). */
const MIN_GRID_LOCATIONS = 4;
/** Distinct rows and columns a grid needs on each axis. */
const MIN_AXIS_VALUES = 2;
/** Coordinates closer than this are the same location (≈1 mm). */
const COINCIDENT_DEG = 1e-8;
/** How far off the lattice a point may sit, as a fraction of the step. */
const LATTICE_TOLERANCE = 0.15;
/** Share of neighbour gaps that must match the smallest gap. */
const CONSISTENT_GAP_SHARE = 0.7;
/** Share of points that must land on the lattice on both axes. */
const ON_LATTICE_SHARE = 0.9;

const FALLBACK_STEP = 0.001;

const isFinite_ = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Candidate spacing for one axis from its distinct sorted values, or null
 * when the gaps between neighbours are not regular enough to be a grid.
 */
const axisStep = (sortedUnique: number[]): number | null => {
  if (sortedUnique.length < MIN_AXIS_VALUES) return null;
  const diffs: number[] = [];
  for (let i = 1; i < sortedUnique.length; i++) {
    diffs.push(sortedUnique[i] - sortedUnique[i - 1]);
  }
  const minDiff = Math.min(...diffs);
  if (!(minDiff > COINCIDENT_DEG)) return null;
  const consistent =
    diffs.filter((d) => Math.abs(d - minDiff) < minDiff * 0.1).length / diffs.length;
  return consistent >= CONSISTENT_GAP_SHARE ? minDiff : null;
};

/** Does `value` sit (within tolerance) on the lattice origin + k·step? */
const onLattice = (value: number, origin: number, step: number): boolean => {
  const k = (value - origin) / step;
  return Math.abs(k - Math.round(k)) <= LATTICE_TOLERANCE;
};

export const detectIgnitionGrid = (data: GridCandidate): GridDetection => {
  const fallback: GridDetection = {
    latStep: data.latStep || FALLBACK_STEP,
    lngStep: data.lngStep || FALLBACK_STEP,
    isGridded: false,
  };

  // Distinct locations only: ensemble members and repeated regimes stack
  // several ignitions on the same point, and duplicates say nothing about
  // the layout.
  const seen = new Set<string>();
  const points: Array<{ lat: number; lng: number }> = [];
  for (const ig of data.ignitions) {
    if (!isFinite_(ig.lat) || !isFinite_(ig.lng)) continue;
    const key = `${ig.lat.toFixed(8)},${ig.lng.toFixed(8)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    points.push({ lat: ig.lat, lng: ig.lng });
  }
  if (points.length < MIN_GRID_LOCATIONS) return fallback;

  const unique = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b);
    const out: number[] = [];
    for (const v of sorted) {
      if (out.length === 0 || v - out[out.length - 1] > COINCIDENT_DEG) out.push(v);
    }
    return out;
  };
  const lats = unique(points.map((p) => p.lat));
  const lngs = unique(points.map((p) => p.lng));

  // A grid has rows AND columns. A single row of ignitions is a line of
  // points and is drawn as such.
  if (lats.length < MIN_AXIS_VALUES || lngs.length < MIN_AXIS_VALUES) return fallback;

  const latStep = axisStep(lats);
  const lngStep = axisStep(lngs);
  if (latStep == null || lngStep == null) return fallback;

  // Every location must sit on the lattice. Regular gaps alone are not
  // enough: a handful of scattered points can have gaps that happen to
  // match while the points themselves are nowhere near a common lattice.
  const lat0 = lats[0];
  const lng0 = lngs[0];
  const onGrid = points.filter(
    (p) => onLattice(p.lat, lat0, latStep) && onLattice(p.lng, lng0, lngStep)
  ).length;
  if (onGrid / points.length < ON_LATTICE_SHARE) return fallback;

  return { latStep, lngStep, isGridded: true };
};

/** Axis-aligned square cell centred on a point, `latHalf`/`lngHalf` each side. */
export const squareCell = (
  lat: number,
  lng: number,
  latHalf: number,
  lngHalf: number
): GeoJSON.Polygon => ({
  type: "Polygon",
  coordinates: [
    [
      [lng - lngHalf, lat - latHalf],
      [lng + lngHalf, lat - latHalf],
      [lng + lngHalf, lat + latHalf],
      [lng - lngHalf, lat + latHalf],
      [lng - lngHalf, lat - latHalf],
    ],
  ],
});
